import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const PUBLIC_VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export const usePushNotifications = () => {
    const { user } = useAuth();
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [permission, setPermission] = useState<'default' | 'granted' | 'denied'>(() => {
        if (typeof window !== 'undefined' && 'Notification' in window) {
            return Notification.permission;
        }
        return 'default';
    });
    const [isSupported, setIsSupported] = useState(false);

    useEffect(() => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            setIsSupported(true);
            checkSubscription();
        }
    }, []);

    const urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const checkSubscription = async () => {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
        } catch (error) {
            console.error('Error checking push subscription:', error);
        }
    };

    const subscribe = async () => {
        if (!user) {
            alert("Debes iniciar sesión primero.");
            return;
        }

        try {
            if (typeof window === 'undefined' || !('Notification' in window)) {
                alert("Las notificaciones push no están soportadas en este navegador.");
                return false;
            }

            const result = await Notification.requestPermission();
            setPermission(result);

            if (result === 'granted') {
                const registration = await navigator.serviceWorker.ready;

                let subscription = await registration.pushManager.getSubscription();
                if (!subscription) {
                    subscription = await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
                    });
                }

                // Parse Subscription to JSON
                const subJSON = subscription.toJSON();
                if (!subJSON.endpoint || !subJSON.keys) throw new Error("Subscription failed");

                // Save to Supabase
                const { error } = await supabase.from('push_subscriptions').upsert({
                    user_id: user.id,
                    endpoint: subJSON.endpoint,
                    p256dh: subJSON.keys.p256dh,
                    auth: subJSON.keys.auth
                }, { onConflict: 'endpoint' });

                if (error) throw error;
                
                setIsSubscribed(true);
                return true;
            } else {
                alert("Permiso de notificaciones denegado.");
                return false;
            }
        } catch (error: any) {
            console.error('Error subscribing to push:', error);
            alert("Error al activar notificaciones: " + error.message);
            return false;
        }
    };

    return { isSupported, permission, isSubscribed, subscribe };
};
